import { JsonProperty, ObjectMapper } from "json-object-mapper";
import { PromotionRequestType } from "services/enums/PromotionRequestType.enum";
import { ApprovalScopeCustomFieldModel } from "services/models/corporate/approval-scope/approval-scope-custom-field.model";
import { ApprovalScopeActionType } from "services/enums/approvalScopeActionType.enum";
import { IntegrationLinkTypeEnum } from "services/enums/integrationLinkType.enum";
import { StandardFieldValidationResolvedModel } from "services/models/corporate/approval-scope/standard-field-validation.model";
import { WorkforceplanMovementType } from "services/enums/WorkforceplanMovementType.enum";
import { WorkforceplanSolicitationStatus } from "services/enums/WorkforceplanSolicitationStatus.enum";
import { IModel } from "services/models/IModel";
import { Model } from "services/models/model";
import { WorkforcePlanWorkScaleGroupModel } from "./workforceplan-workscale-group.model";
import { MovementRequestFileModel } from "./movement-request-file.model";

export interface WorkforcePlanApprovalDetails {
	workScaleGroups: WorkforcePlanWorkScaleGroupModel[];
	version?: number | string;
	companyName?: string;
	costCenterName?: string;
	totalEffective?: number;
}

export class TrainingCompletionGateStatus {
	@JsonProperty()
	isActive: boolean = false;

	@JsonProperty()
	canEdit: boolean = true;

	@JsonProperty()
	minimumTrainingDays?: number;

	@JsonProperty()
	eligibleDate?: string;

	@JsonProperty()
	blockMessage: string = "";
}

export class WorkforcePlanSolicitationDetailsModel extends Model implements IModel<WorkforcePlanSolicitationDetailsModel> {
	note: string = "";
	payload: string = "";
	approvalScopeItemId: string = "";
	employeeAdmissionDate: string = "";
	employeeCostCenterId: string = "";
	employeeCostCenterCode: string = "";
	employeeCostCenterName: string = "";
	employeeCPF: string = "";
	employeeIsPCD: string = "";
	employeeJobTitleId: string = "";
	employeeJobTitleName: string = "";
	employeeRegistration: string = "";
	employeeTimeShiftCode: string = "";
	employeeTimeShiftSequence: string = "";
	employeeTimeShiftTitle: string = "";
	employeeTimeShiftId: string = "";
	requesterId: string = "";
	requesterName: string = "";
	requesterImage: string = "";
	stepEffectiveDate: string = "";
	stepNote: string = "";
	stepStatus: string = "";
	stepName: string = "";
	stepActionType: string = "";
	stepActionTypes: string[] = [];
	erpIntegrationLinkTypes: Array<string | number> = [];
	unhealthinessFilledByDedicatedStep: boolean = false;

	permission: { canExecute: boolean; canUpdate: boolean; canStop: boolean; } = {
		canExecute: false, canUpdate: false, canStop: false
	}

	approvalDetails?: WorkforcePlanApprovalDetails = {
		workScaleGroups: []
	}
	customFields: ApprovalScopeCustomFieldModel[] = [];
	files: MovementRequestFileModel[] = [];
	standardFieldValidations: StandardFieldValidationResolvedModel[] = [];
	hiddenStandardFields: string[] = [];

	@JsonProperty({ type: TrainingCompletionGateStatus })
	trainingCompletionGate: TrainingCompletionGateStatus | null = null;

	convertedFromMovementRequestId: string = "";
	convertedToMovementRequestId: string = "";
	canConvertAptitudeTraining: boolean = false;
	canCancel: boolean = false;
	canceledAt: string | null = null;
	cancellationReason: string = "";
	canceledByName: string = "";

	get statusBadge(): string {
		const status = WorkforceplanSolicitationStatus.getBy(this.stepStatus);

		return `<span class="badge bg-${status?.className} text-wrap">${status?.label}</span>`;
	}

	get statusData(): WorkforceplanSolicitationStatus {
		return WorkforceplanSolicitationStatus.getBy(this.stepStatus)
	}

	get movementTypeData(): WorkforceplanMovementType {
		return WorkforceplanMovementType.getByAny((this as any).movementType) as WorkforceplanMovementType
	}

	get payloadData() {
		return JSON.parse(this.payload)
	}

	get movementTypeLabel() {
		if (this.payloadData.raiseType) {
			return this.payloadData.raiseType
		}

		if (this.payloadData.promotionType) {
			const promotionTypeLabel =
				PromotionRequestType.getByAny(this.payloadData.promotionType)?.label ?? this.payloadData.promotionType;
			return `${this.movementTypeData?.label || "Promoção"} / ${promotionTypeLabel}`;
		}

		return this.movementTypeData?.label ?? "Solicitação"
	}

	get resolvedStepActionTypes(): string[] {
		const actionTypes = Array.isArray(this.stepActionTypes)
			? this.stepActionTypes
				.map(actionType => this.normalizeStepActionType(actionType))
				.filter((actionType): actionType is string => !!actionType)
			: [];

		if (actionTypes.length > 0) {
			return actionTypes;
		}

		const fallbackActionType = this.normalizeStepActionType(this.stepActionType);
		return fallbackActionType ? [fallbackActionType] : [];
	}

	hasStepActionType(actionType: string): boolean {
		return this.resolvedStepActionTypes.includes(actionType);
	}

	hasErpIntegrationAction(): boolean {
		return this.hasStepActionType(ApprovalScopeActionType.ERP_INTEGRATION.value);
	}

	requiresTradeUnionForErp(): boolean {
		if (!this.hasErpIntegrationAction()) {
			return false;
		}

		const linkTypes = Array.isArray(this.erpIntegrationLinkTypes) ? this.erpIntegrationLinkTypes : [];
		return linkTypes.some(
			type =>
				type === IntegrationLinkTypeEnum.CHANGE_JOB_TITLE.value ||
				Number(type) === IntegrationLinkTypeEnum.CHANGE_JOB_TITLE.id
		);
	}

	requiresChangeEmployeeParamsForErp(): boolean {
		if (!this.hasErpIntegrationAction()) {
			return false;
		}

		const linkTypes = Array.isArray(this.erpIntegrationLinkTypes) ? this.erpIntegrationLinkTypes : [];
		return linkTypes.some(
			type =>
				type === IntegrationLinkTypeEnum.CHANGE_EMPLOYEE_PARAMS.value ||
				Number(type) === IntegrationLinkTypeEnum.CHANGE_EMPLOYEE_PARAMS.id
		);
	}

	private normalizeStepActionType(actionType: unknown): string | null {
		if (typeof actionType === "string") {
			const numericActionType = Number(actionType);
			if (!Number.isNaN(numericActionType) && actionType.trim() !== "") {
				return (ApprovalScopeActionType.getById(numericActionType)?.value as string) ?? actionType;
			}

			return actionType;
		}

		if (typeof actionType === "number") {
			return (ApprovalScopeActionType.getById(actionType)?.value as string) ?? String(actionType);
		}

		return null;
	}

	static create(input: any): WorkforcePlanSolicitationDetailsModel {
		if ('customFields' in (input || {})) {
			input.customFields = (input.customFields || []).map(ApprovalScopeCustomFieldModel.create);
		}

		if ('files' in (input || {})) {
			input.files = (input.files || []).map(MovementRequestFileModel.create);
		}

		if ('standardFieldValidations' in (input || {})) {
			input.standardFieldValidations = (input.standardFieldValidations || []).map(
				StandardFieldValidationResolvedModel.create
			);
		}

		const model = ObjectMapper.deserialize<WorkforcePlanSolicitationDetailsModel>(
			WorkforcePlanSolicitationDetailsModel,
			input || {}
		);

		// ObjectMapper ignora propriedades complexas sem valor inicial em Object.keys();
		// deserializa o gate explicitamente (mesmo padrão de nested objects do projeto).
		model.trainingCompletionGate = input?.trainingCompletionGate
			? ObjectMapper.deserialize(TrainingCompletionGateStatus, input.trainingCompletionGate)
			: null;

		return model;
	}

	public toJSON(): string {
		return ObjectMapper.serialize(this) as string;
	}
}
